import { useEffect, useRef, useState } from 'react'
import { getBackendBaseUrl } from '../../../../../helpers/backendUrl'
import { useDispatch } from 'react-redux'

import { AiFillThunderbolt } from 'react-icons/ai'
import {
    Card,
    Divider,
    Modal,
    Progress,
    Typography,
    Upload,
    message,
} from 'antd'

import Button from '../../../../../components/button/button'
import {
    uploadResumePublic,
    uploadResumeS3Url,
} from '../../../../candidate/service/candidate.service'
import {
    clearCandidateDetails,
    setCandidateId,
    setCandidateResumeUrl,
    setResumeFile,
} from '../../../../candidate/store/candidate.slice'
import {
    setEducationDraft,
    setExperienceDraft,
    setOverviewDraft,
} from '../../store/applicationDraft.slice'
import { replaceKeys } from '../../../../../helpers/jobs'

import './index.css'
import ResumeProcess from '../../../../../assets/svg/resume-process'

const ImportResume = () => {
    const { Text, Title } = Typography
    const dispatch = useDispatch()

    const [loading, setLoading] = useState(false)
    const [showUploadModal, setShowUploadModal] = useState(false)
    const [uploadStep, setUploadStep] = useState(0)

    const [progress, setProgress] = useState(1)
    const [targetProgress, setTargetProgress] = useState(1)
    const rafRef = useRef(null)
    const timersRef = useRef([])

    const steps = [
        'Reading your resume',
        'Filling in your contact details',
        'Adding your experience and education',
        'Finishing up',
    ]

    const STEP_CAP = [35, 65, 70, 92]

    const cancelAnim = () => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current)
        rafRef.current = null
    }

    const animate = () => {
        setProgress((prev) => {
            const diff = targetProgress - prev
            if (Math.abs(diff) < 0.2) return targetProgress
            return prev + diff * 0.08
        })
        rafRef.current = requestAnimationFrame(animate)
    }

    useEffect(() => {
        if (!showUploadModal) {
            cancelAnim()
            setProgress(1)
            setTargetProgress(1)
            timersRef.current.forEach(clearTimeout)
            timersRef.current = []
            return
        }

        cancelAnim()
        rafRef.current = requestAnimationFrame(animate)

        timersRef.current.push(
            window.setTimeout(() => setUploadStep((s) => Math.max(s, 1)), 1200)
        )
        timersRef.current.push(
            window.setTimeout(() => setUploadStep((s) => Math.max(s, 2)), 4000)
        )

        return () => {
            cancelAnim()
            timersRef.current.forEach(clearTimeout)
            timersRef.current = []
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showUploadModal])

    useEffect(() => {
        const cap = STEP_CAP[Math.min(uploadStep, STEP_CAP.length - 1)]
        setTargetProgress((prev) => Math.max(prev, cap))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadStep])

    const beforeUpload = (file) => {
        const isPDF = file.type === 'application/pdf'
        if (!isPDF) message.error('You can only upload PDF files!')
        const isLt5M = file.size / 1024 / 1024 < 5
        if (!isLt5M) message.error('File must be smaller than 5MB!')
        return isPDF && isLt5M
    }

    const handleUploadPdf = async (fileWrap) => {
        try {
            const payload = {
                data: [
                    {
                        filename: fileWrap?.file?.name,
                        contentType: fileWrap?.file?.type,
                    },
                ],
            }
            const { data } = await uploadResumePublic(payload)
            if (data.success) {
                uploadFileS3Url(
                    fileWrap?.file,
                    data?.data[0]?.uploadUrl,
                    data?.data[0]?.publicLink,
                    fileWrap?.file?.name
                )
            }
        } catch (error) {
            console.log('🚀 ~ handleUploadPdf ~ error:', error)
        }
    }

    const uploadFileS3Url = async (file, uploadUrl, publicUrl, fileName) => {
        try {
            const data = await uploadResumeS3Url(file, uploadUrl)
            if (data?.status === 200) {
                handleAiUploadUrl(publicUrl, 'application/pdf', fileName, file)
            }
        } catch (error) {
            console.log('🚀 ~ uploadFileS3Url ~ error:', error)
        }
    }

    const closeModalSoon = (delay = 1200) => {
        timersRef.current.push(
            window.setTimeout(() => {
                setShowUploadModal(false)
                setLoading(false)
            }, delay)
        )
    }

    const handleAiUploadUrl = async (url, type, fileName, file) => {
        try {
            const payload = {
                resumes: [
                    {
                        resumeUrl: url,
                        fileType: type,
                        fileName: fileName,
                    },
                ],
            }

            const response = await fetch(
                getBackendBaseUrl() + '/candidate/cv/upload',
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                    credentials: 'include',
                }
            )

            const reader = response.body?.getReader()
            if (!reader) throw new Error('No readable stream from server')

            const decoder = new TextDecoder('utf-8')
            let candidate = null

            while (true) {
                const { done, value } = await reader.read()
                if (done) break

                const chunk = decoder.decode(value, { stream: true })

                // eslint-disable-next-line no-loop-func
                chunk.split('\n').forEach(async (line) => {
                    if (!line.startsWith('data:')) return
                    try {
                        const json = JSON.parse(line.replace('data: ', ''))

                        if (
                            json?.error ===
                            'Candidate already exists with this email'
                        ) {
                            setTargetProgress(100)
                            closeModalSoon(600)
                            message.error(json.error)
                            return
                        }
                        if (
                            json?.error ===
                            'Email is not present in resume, check resume file and try again'
                        ) {
                            setTargetProgress(100)
                            dispatch(setCandidateId(null))
                            dispatch(clearCandidateDetails())
                            dispatch(setResumeFile(file))
                            dispatch(setCandidateResumeUrl(url))
                            closeModalSoon(600)
                            message.error(json.error)
                            return
                        }

                        if (json?.error) {
                            setTargetProgress(100)
                            closeModalSoon(600)
                            message.error(json.error || 'An error occurred')
                            return
                        }

                        if (json?.candidate) candidate = json.candidate

                        if (json?.message?.includes('uploaded'))
                            setUploadStep(1)
                        if (json?.message?.includes('details')) setUploadStep(2)
                        if (json?.message?.includes('education'))
                            setUploadStep(3)

                        if (json?.message?.includes('successfully')) {
                            setUploadStep(steps.length - 1)
                            setTargetProgress(100)
                            closeModalSoon(1200)

                            if (candidate) {
                                dispatch(setCandidateId(candidate))

                                console.log(candidate)

                                dispatch(setOverviewDraft({}))
                                dispatch(setEducationDraft([]))
                                dispatch(setExperienceDraft([]))

                                // const overviewData = await getCandidateById(
                                //     candidate,
                                //     'OVERVIEW'
                                // )
                                // if (overviewData?.data?.success) {
                                const overViewProfile = candidate
                                dispatch(setOverviewDraft(overViewProfile))
                                // }

                                // const educationData = await getCandidateById(
                                //     candidate,
                                //     'EDUCATION'
                                // )
                                // if (educationData?.data?.success) {
                                //     const educationProfile =
                                //         educationData?.data?.data
                                const transformedEducation = replaceKeys(
                                    candidate?.education,
                                    {
                                        name: 'label',
                                        _id: 'value',
                                    }
                                )
                                dispatch(
                                    setEducationDraft(
                                        transformedEducation.map((data) => ({
                                            ...data,
                                            _tempId: crypto.randomUUID(),
                                        }))
                                    )
                                )
                                // }

                                // const experienceData = await getCandidateById(
                                //     candidate,
                                //     'EXPERIENCE'
                                // )
                                // if (experienceData?.data?.success) {
                                //     const experienceProfile =
                                //         experienceData?.data?.data
                                const transformedExperience = replaceKeys(
                                    candidate?.experience,
                                    {
                                        name: 'label',
                                        _id: 'value',
                                    }
                                )
                                dispatch(
                                    setExperienceDraft(
                                        transformedExperience.map((data) => ({
                                            ...data,
                                            _tempId: crypto.randomUUID(),
                                        }))
                                    )
                                )
                                // }
                            }
                        }
                    } catch (err) {
                        console.warn('Invalid JSON in stream:', line)
                    }
                })
            }
        } catch (error) {
            console.error('Upload failed:', error)

            setTargetProgress(100)
            closeModalSoon(600)
            message.error('Upload failed. Please try again.')
        }
    }

    const handleChange = (info) => {
        if (info?.file?.status === 'uploading') {
            setLoading(true)
            setShowUploadModal(true)
            setUploadStep(0)
            setProgress(1)
            setTargetProgress(70)
            return
        }

        if (info?.file?.status === 'done') {
            handleUploadPdf(info)
        } else if (info?.file?.status === 'error') {
            message.error('Upload failed. Please try again.')
            setLoading(false)
            setShowUploadModal(false)
        }
    }

    const dummyRequest = ({ onSuccess }) => {
        setTimeout(() => onSuccess('ok'), 1000)
    }

    return (
        <>
            <Card
                className="import-resume-card"
                styles={{ body: { padding: 0 } }}
            >
                <div
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '12px',
                    }}
                >
                    <div>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                            }}
                        >
                            <AiFillThunderbolt size={18} color="#4f46e5" />
                            <Text
                                strong
                                style={{
                                    fontSize: '13px',
                                    fontWeight: 700,
                                    color: 'var(--textColorDarkSlate)',
                                    letterSpacing: '0.02em',
                                }}
                            >
                                Autofill application
                            </Text>
                        </div>
                        <div
                            style={{
                                marginTop: '6px',
                                fontSize: '13px',
                                color: '#64748b',
                                lineHeight: 1.45,
                            }}
                        >
                            Save time by importing your resume in one of the
                            following formats: .pdf.
                        </div>
                    </div>

                    <Upload
                        customRequest={dummyRequest}
                        name="pdf"
                        showUploadList={false}
                        accept=".pdf"
                        beforeUpload={beforeUpload}
                        onChange={handleChange}
                        disabled={loading}
                    >
                        <Button
                            props={{
                                text: 'Import Resume',
                                style: {
                                    borderRadius: '999px',
                                    height: '36px',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    background: '#4f46e5',
                                    borderColor: '#4f46e5',
                                    padding: '0 16px',
                                },
                                onClick: () => {},
                            }}
                        />
                    </Upload>
                </div>
            </Card>

            <Title level={4}>Personal Information</Title>
            <Divider style={{ marginTop: '10px' }} />

            <Modal
                open={showUploadModal}
                footer={null}
                closable={false}
                centered
                styles={{
                    body: { padding: 0, borderRadius: 16 },
                    mask: { backgroundColor: 'rgba(16,24,40,0.7)' },
                }}
                className="resume-modal"
            >
                <div className="rp-wrap">
                    <div className="rp-icon-outer">
                        <ResumeProcess />
                    </div>

                    <h2 className="rp-title">Processing your resume</h2>

                    <div className="rp-progress">
                        <Progress
                            percent={Math.min(100, Math.round(progress))}
                            showInfo={false}
                            strokeLinecap="round"
                            strokeColor={{ '0%': '#6366f1', '100%': '#4f46e5' }}
                            trailColor="#e0e7ff"
                        />
                        <div className="rp-progress-label">
                            {Math.min(100, Math.round(progress))}% completed
                        </div>
                    </div>

                    <ul className="rp-steps">
                        {steps.map((s, idx) => {
                            const done = idx <= uploadStep
                            return (
                                <li key={idx} className="rp-step">
                                    <span
                                        className={`rp-dot ${
                                            done ? 'rp-dot--on' : ''
                                        }`}
                                    />
                                    <span
                                        className={`rp-step-text ${
                                            done ? 'rp-step-text--on' : ''
                                        }`}
                                    >
                                        {s}
                                    </span>
                                </li>
                            )
                        })}
                    </ul>
                </div>
            </Modal>
        </>
    )
}

export default ImportResume
